import { useQuery, useMutation } from "@tanstack/react-query";
import type {
  CreateUserRequest,
  GetUsersCountRequest,
  GetUsersCountResponse,
  GetUsersRequest,
  User,
} from "../../../api/client/types/user.types";
import type {
  PaginatedResponse,
  ApiError,
} from "../../../api/client/types/common.types";
import { userEndpoints } from "../../../api/endpoints/user";

/**
 * React Query Keys
 * Organized keys help with cache invalidation and devtools debugging.
 */
const userQueryKeys = {
  root: ["users"] as const,
  list: (params?: GetUsersRequest) =>
    [...userQueryKeys.root, "list", params] as const,
  detail: (id: string) => [...userQueryKeys.root, "detail", id] as const,
  create: () => [...userQueryKeys.root, "create"] as const,
  update: (id: string) => [...userQueryKeys.root, "update", id] as const,
  count: (params?: GetUsersCountRequest) => [...userQueryKeys.root, "count", params] as const,
};

/**
 * Hook: useGetUsers
 * Fetches a paginated list of users with optional filters.
 */
export const useGetUsers = (params?: GetUsersRequest) => {
  return useQuery<PaginatedResponse<User>, ApiError>({
    queryKey: userQueryKeys.list(params),
    queryFn: () => userEndpoints.getUsers(params),
  });
};

/**
 * Hook: useGetUserById
 * Fetches a single user by ID.
 */
export const useGetUserById = (id: string) => {
  return useQuery<User, ApiError>({
    queryKey: userQueryKeys.detail(id),
    queryFn: () => userEndpoints.getUserById(id),
    enabled: !!id, // Only run if id is provided
  });
};

/**
 * Hook: useCreateUser
 * Creates a new user.
 */
export const useCreateUser = () => {
  return useMutation<User, ApiError, CreateUserRequest>({
    mutationKey: userQueryKeys.create(),
    mutationFn: (data) => userEndpoints.createUser(data),
  });
};

/**
 * Hook: useUpdateUser
 * Updates an existing user.
 */
export const useUpdateUser = () => {
  return useMutation<User, ApiError, { id: string; data: CreateUserRequest }>({
    mutationKey: userQueryKeys.update("generic"), // key uses 'generic' to avoid dynamic issues
    mutationFn: ({ id, data }) => userEndpoints.updateUser(id, data),
  });
};

/**
 * Hook: useGetUsersCount
 * Fetches the count of users with optional filters.
 */
export const useGetUsersCount = (params?: GetUsersCountRequest) => {
  return useQuery<GetUsersCountResponse, ApiError>({
    queryKey: userQueryKeys.count(params),
    queryFn: () => userEndpoints.getUsersCount(params),
  });
};